import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  ArrowLeft,
  Clock,
  User,
  MapPin,
  Wrench,
  FileText,
  Image,
  DollarSign,
  CheckCircle2,
  XCircle,
  RotateCcw,
  AlertCircle,
  Mail,
  Trash2,
  Users,
  UserCheck,
  Bell,
  AlertTriangle
} from "lucide-react";
import { format } from "date-fns";
import CostEstimateDialog, { canSubmitCostEstimate } from "@/components/CostEstimateDialog";

const STATUS_COLORS = {
  "Troubleshooting in Progress": { bg: "#DBEAFE", text: "#1E40AF" },
  "Escalated to Help Desk": { bg: "#DBEAFE", text: "#1E40AF" },
  "Assigned to Technician": { bg: "#FEF9C3", text: "#854D0E" },
  "Vendor Assigned": { bg: "#FEF9C3", text: "#854D0E" },
  "Pending Cost Approval": { bg: "#FFEDD5", text: "#9A3412" },
  "Repair Authorized": { bg: "#EEF2FF", text: "#4338CA" },
  "Resolved": { bg: "#DCFCE7", text: "#166534" },
  "Resolved by Manager": { bg: "#DCFCE7", text: "#166534" },
  "Rejected - Needs Review": { bg: "#FEE2E2", text: "#991B1B" },
  "Cancelled": { bg: "#F3F4F6", text: "#6B7280" },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TicketDetail() {
  const { ticketId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checklist, setChecklist] = useState([]);
  const [checklistLoaded, setChecklistLoaded] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [estimateDialogOpen, setEstimateDialogOpen] = useState(false);
  const [adminEditEstimateMode, setAdminEditEstimateMode] = useState(false);
  const [adminEditConfirmOpen, setAdminEditConfirmOpen] = useState(false);
  const [canConfirmPayment, setCanConfirmPayment] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [escalateDialogOpen, setEscalateDialogOpen] = useState(false);
  const [marketTechs, setMarketTechs] = useState([]);
  const [opsUsers, setOpsUsers] = useState([]);
  const [selectedTechEmail, setSelectedTechEmail] = useState("");
  const [selectedOpsUserId, setSelectedOpsUserId] = useState("");
  // Unified escalate picker: "ops:<id>" or "tech:<email>"
  const [escalatePick, setEscalatePick] = useState("");
  // Purchase-details gate (shown after troubleshoot checklist, before escalate dialog)
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [purchaseLinks, setPurchaseLinks] = useState([{ url: "", note: "" }]);
  const [purchaseNewPhotos, setPurchaseNewPhotos] = useState([]);
  const [purchaseSubmitting, setPurchaseSubmitting] = useState(false);
  // true  → dialog opened from "Edit Purchase Research" on an already-active ticket:
  //         show only the Save button, no "Technician will source" skip, no auto-escalate.
  // false → dialog opened from "Issue Not Resolved - Escalate to Technician" on troubleshoot:
  //         show the full flow (skip button + auto-open the Escalate picker on submit).
  const [purchaseEditMode, setPurchaseEditMode] = useState(false);
  // Reassign-ticket dialog (SystemAdmin / President / District Manager / District Operations)
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [reassignTargetId, setReassignTargetId] = useState("");
  const [reassignReason, setReassignReason] = useState("");
  const [reassignSubmitting, setReassignSubmitting] = useState(false);
  const REASSIGN_ROLES = [
    "SystemAdmin",
    "President",
    "District Manager",
    "District Operations",
    "Operations/DM", // legacy aliases
    "Operations/DO",
  ];
  const canReassign = !!user?.role && REASSIGN_ROLES.includes(user.role);
  const [reminderSending, setReminderSending] = useState(false);

  useEffect(() => {
    fetchTicket();
    fetchPaymentPermission();
  }, [ticketId]);

  const fetchPaymentPermission = async () => {
    try {
      const res = await api.get(`/tickets/${ticketId}/payment-permission`);
      setCanConfirmPayment(!!res.data?.can_confirm_payment);
    } catch (e) {
      setCanConfirmPayment(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!paymentDate) {
      toast.error("Please pick a payment date");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/tickets/${ticketId}/payment-confirm`, {
        payment_date: paymentDate,
        reference: paymentReference || null,
        notes: paymentNotes || null,
      });
      toast.success("Payment confirmed");
      setPaymentDialogOpen(false);
      setPaymentDate("");
      setPaymentReference("");
      setPaymentNotes("");
      fetchTicket();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to confirm payment");
    } finally {
      setSubmitting(false);
    }
  };

  const fetchTicket = async () => {
    try {
      const res = await api.get(`/tickets/${ticketId}`);
      setTicket(res.data);
      // Load troubleshooting checklist if ticket is in troubleshooting
      if (res.data.status === "Troubleshooting in Progress" && res.data.equipment_category) {
        fetchChecklist(res.data.equipment_category);
      }
      // Load market technicians AND DO/DM operations users for the escalation picker
      if (res.data.unit_id) {
        try {
          const techRes = await api.get(`/markets/${encodeURIComponent(res.data.unit_id)}/technicians`);
          setMarketTechs(techRes.data.technicians || []);
        } catch {
          setMarketTechs([]);
        }
        try {
          const opsRes = await api.get(`/markets/${encodeURIComponent(res.data.unit_id)}/operations-users`);
          setOpsUsers(opsRes.data.operations_users || []);
        } catch {
          setOpsUsers([]);
        }
        if (canReassign) {
          try {
            const aRes = await api.get(`/markets/${encodeURIComponent(res.data.unit_id)}/assignable-users`);
            setAssignableUsers(aRes.data.users || []);
          } catch {
            setAssignableUsers([]);
          }
        }
      }
    } catch (error) {
      toast.error("Failed to load ticket");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const fetchChecklist = async (category) => {
    try {
      const res = await api.get(`/checklists/${encodeURIComponent(category)}`);
      if (res.data && res.data.items) {
        setChecklist(res.data.items.map(item => ({ text: item, checked: false })));
      }
      setChecklistLoaded(true);
    } catch (error) {
      console.error("Failed to load checklist:", error);
      setChecklistLoaded(true); // Still mark loaded so buttons show
    }
  };

  const handleTroubleshootingSubmit = async (resolved, assigneeEmail = null, successMessage = null) => {
    setSubmitting(true);
    try {
      await api.post(`/tickets/${ticketId}/troubleshooting`, {
        checklist_items: checklist.map(item => ({ text: item.text, checked: item.checked })),
        resolved,
        assignee_email: assigneeEmail || null,
      });
      if (resolved) {
        toast.success("Ticket marked as resolved");
      } else if (successMessage) {
        toast.success(successMessage);
      } else if (assigneeEmail) {
        toast.success(`Ticket assigned to ${assigneeEmail}`);
      } else {
        toast.success("Ticket made available to all market technicians");
      }
      setEscalateDialogOpen(false);
      setSelectedOpsUserId("");
      setSelectedTechEmail("");
      setEscalatePick("");
      fetchTicket();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit troubleshooting");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEscalateViaOpsUser = () => {
    const picked = opsUsers.find((o) => o.id === selectedOpsUserId);
    if (!picked) return;
    const techEmail = picked.default_technician_email;
    if (!techEmail) {
      toast.error(
        `${picked.full_name} has no default technician set. Pick someone else, or use "all market technicians" below.`
      );
      return;
    }
    handleTroubleshootingSubmit(
      false,
      techEmail,
      `Routed via ${picked.full_name} → ${picked.default_technician_name || techEmail}`
    );
  };

  // Unified picker submit: value is either "ops:<id>" or "tech:<email>".
  const handleEscalateUnified = () => {
    if (!escalatePick) return;
    const [kind, raw] = escalatePick.split(":");
    if (kind === "ops") {
      const picked = opsUsers.find((o) => o.id === raw);
      if (!picked) return;
      const techEmail = picked.default_technician_email;
      if (!techEmail) {
        toast.error(
          `${picked.full_name} has no default technician set. Pick a technician directly, or use "all market technicians" below.`
        );
        return;
      }
      handleTroubleshootingSubmit(
        false,
        techEmail,
        `Routed via ${picked.full_name} → ${picked.default_technician_name || techEmail}`
      );
    } else if (kind === "tech") {
      handleTroubleshootingSubmit(false, raw);
    }
  };

  const handleCancelTicket = async () => {
    if (!cancelReason.trim()) {
      toast.error("Please provide a cancellation reason");
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("reason", cancelReason);
      await api.post(`/tickets/${ticketId}/cancel`, formData);
      toast.success("Ticket cancelled");
      setCancelDialogOpen(false);
      fetchTicket();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel ticket");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim()) return;
    try {
      await api.post(`/tickets/${ticketId}/notes`, { content: noteContent });
      toast.success("Note added");
      setNoteContent("");
      fetchTicket();
    } catch (error) {
      toast.error("Failed to add note");
    }
  };

  const handleSubmitPurchaseDetails = async (sourcedByTechnician = false) => {
    const cleanedLinks = purchaseLinks
      .map((l) => ({ url: (l.url || "").trim(), note: (l.note || "").trim() }))
      .filter((l) => l.url);
    const existingPhotos = ticket?.photos?.length || 0;
    if (
      !sourcedByTechnician &&
      cleanedLinks.length === 0 &&
      purchaseNewPhotos.length === 0 &&
      existingPhotos === 0
    ) {
      toast.error("Add at least one purchase link or upload at least one photo.");
      return;
    }
    setPurchaseSubmitting(true);
    try {
      // 1) Upload any new photos first (reuse existing /photos endpoint)
      if (!sourcedByTechnician && purchaseNewPhotos.length > 0) {
        const fd = new FormData();
        purchaseNewPhotos.forEach((p) => fd.append("files", p));
        fd.append("stage", "purchase_details");
        await api.post(`/tickets/${ticketId}/photos?stage=purchase_details`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      // 2) Submit the purchase-details record (links + sourcing flag)
      await api.post(`/tickets/${ticketId}/purchase-details`, {
        links: sourcedByTechnician ? [] : cleanedLinks,
        sourced_by_technician: sourcedByTechnician,
      });
      toast.success(
        sourcedByTechnician
          ? "Technician will source the items — pick a tech next."
          : purchaseEditMode
            ? "Purchase details updated."
            : "Purchase details saved — pick a technician next."
      );
      setPurchaseDialogOpen(false);
      await fetchTicket();
      // Only auto-open the Escalate picker when this dialog was invoked from
      // the troubleshoot "Issue Not Resolved" flow — never on an edit of an
      // already-active ticket (no re-assignment should happen).
      if (!purchaseEditMode) {
        setEscalateDialogOpen(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save purchase details");
    } finally {
      setPurchaseSubmitting(false);
    }
  };

  const handleReassignSubmit = async () => {
    if (!reassignTargetId) {
      toast.error("Pick a user to reassign the ticket to.");
      return;
    }
    setReassignSubmitting(true);
    try {
      const res = await api.post(`/tickets/${ticketId}/reassign`, {
        assignee_id: reassignTargetId,
        reason: reassignReason.trim() || null,
      });
      toast.success(`Reassigned to ${res.data.assignee_name}`);
      setReassignDialogOpen(false);
      setReassignTargetId("");
      setReassignReason("");
      fetchTicket();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reassign ticket");
    } finally {
      setReassignSubmitting(false);
    }
  };

  const handleSendReminder = async () => {
    if (!ticket?.assignee_id) {
      toast.error("No assignee on this ticket yet.");
      return;
    }
    const target = ticket.assignee_name || "the current assignee";
    if (!window.confirm(`Send a reminder email to ${target}?`)) return;
    setReminderSending(true);
    try {
      const res = await api.post(`/tickets/${ticketId}/remind-assignee`);
      toast.success(`Reminder sent to ${res.data.assignee_name || res.data.assignee_email}`);
      fetchTicket();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder");
    } finally {
      setReminderSending(false);
    }
  };

  const handleDeleteTicket = async () => {
    if (deleteConfirmText !== ticket?.job_number) {
      toast.error(`Type "${ticket?.job_number}" to confirm deletion`);
      return;
    }
    setSubmitting(true);
    try {
      await api.delete(`/tickets/${ticketId}`);
      toast.success(`Ticket ${ticket.job_number} permanently deleted`);
      setDeleteDialogOpen(false);
      navigate(`/tickets/${ticket.unit_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete ticket");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendApproval = async () => {
    setSubmitting(true);
    try {
      const res = await api.post(`/tickets/${ticketId}/resend-approval`);
      const recipients = (res.data?.recipients || []).join(", ");
      toast.success(
        `Approval request resent to ${res.data?.role || "approver"}${recipients ? ` (${recipients})` : ""}`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend approval");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendTreasurer = async () => {
    setSubmitting(true);
    try {
      const res = await api.post(`/tickets/${ticketId}/resend-treasurer`);
      const recipients = (res.data?.recipients || []).join(", ");
      toast.success(
        `Payment request resent${recipients ? ` to ${recipients}` : ""}. If the link in the email doesn't work, the treasurer can also mark this ticket as Paid directly from this page.`,
        { duration: 7000 }
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend payment email");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReopen = async () => {
    if (!reopenReason.trim()) {
      toast.error("Please provide a reason for reopening");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("reason", reopenReason);
      
      const res = await api.post(`/tickets/${ticketId}/reopen`, formData);
      toast.success("Follow-up ticket created");
      navigate(`/ticket/${res.data.id}`);
    } catch (error) {
      toast.error("Failed to reopen ticket");
    } finally {
      setSubmitting(false);
      setReopenDialogOpen(false);
    }
  };

  const getStatusStyle = (status) => {
    const style = STATUS_COLORS[status] || { bg: "#F3F4F6", text: "#6B7280" };
    return { backgroundColor: style.bg, color: style.text };
  };

  const getPriorityStyle = (priority) => {
    const styles = {
      P1: "bg-red-100 text-red-700",
      P2: "bg-orange-100 text-orange-700",
      P3: "bg-yellow-100 text-yellow-700",
      P4: "bg-gray-100 text-gray-700",
    };
    return styles[priority] || "bg-gray-100 text-gray-700";
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar unitId={ticket?.unit_id} />
        <main className="flex-1 p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded-lg"></div>
          </div>
        </main>
      </div>
    );
  }

  if (!ticket) return null;

  const canReopen = ticket.status === "Resolved" && 
    (user?.role === "Store Manager" || user?.role === "SystemAdmin");

  const canCancel = !["Resolved", "Cancelled", "Resolved by Manager"].includes(ticket.status) &&
    (user?.id === ticket.created_by ||
     ["SystemAdmin", "District Manager", "District Operations", "Operations/DM", "Operations/DO"].includes(user?.role));

  const isTroubleshooting = ticket.status === "Troubleshooting in Progress" &&
    (user?.id === ticket.created_by || user?.role === "SystemAdmin");

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar unitId={ticket.unit_id} />
      
      <main className="flex-1 p-4 md:p-8 overflow-auto">
        {/* Back Button & Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
                  {ticket.job_number}
                </h1>
                <Badge 
                  className="text-sm"
                  style={getStatusStyle(ticket.status)}
                  data-testid="ticket-status-badge"
                >
                  {ticket.status}
                </Badge>
              </div>
              <p className="text-gray-600">
                {ticket.restaurant_name}
                {ticket.created_by_name && (
                  <span className="text-gray-500"> &middot; Submitted by <span className="font-medium text-gray-700">{ticket.created_by_name}</span></span>
                )}
              </p>
            </div>

            <div className="flex items-center gap-3">
              {ticket.priority && (
                <Badge className={`${getPriorityStyle(ticket.priority)} text-sm`}>
                  {ticket.priority}
                </Badge>
              )}
              {canCancel && (
                <Button
                  variant="outline"
                  onClick={() => setCancelDialogOpen(true)}
                  className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="cancel-ticket-btn"
                >
                  <XCircle className="w-4 h-4" />
                  Cancel Ticket
                </Button>
              )}
              {canReopen && (
                <Button
                  variant="outline"
                  onClick={() => setReopenDialogOpen(true)}
                  className="flex items-center gap-2"
                  data-testid="reopen-ticket-btn"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reopen Ticket
                </Button>
              )}
              {canReassign && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setReassignTargetId("");
                      setReassignReason("");
                      setReassignDialogOpen(true);
                    }}
                    className="flex items-center gap-2 text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                    data-testid="reassign-ticket-btn"
                    title="Reassign this ticket to another user"
                  >
                    <UserCheck className="w-4 h-4" />
                    Reassign
                  </Button>
                  {ticket.assignee_id && (
                    <Button
                      variant="outline"
                      onClick={handleSendReminder}
                      disabled={reminderSending}
                      className="flex items-center gap-2 text-amber-700 border-amber-200 hover:bg-amber-50"
                      data-testid="send-reminder-btn"
                      title={`Email a nudge to ${ticket.assignee_name || "the current assignee"}`}
                    >
                      <Bell className="w-4 h-4" />
                      {reminderSending ? "Sending…" : "Send Reminder"}
                    </Button>
                  )}
                </>
              )}
              {user?.role === "SystemAdmin" && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setDeleteConfirmText("");
                    setDeleteDialogOpen(true);
                  }}
                  className="flex items-center gap-2 text-red-700 border-red-300 hover:bg-red-50"
                  data-testid="delete-ticket-btn"
                  title="Permanently delete this ticket (SystemAdmin only)"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Ticket Info */}
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-indigo-600" />
                  Ticket Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Equipment Category</label>
                    <p className="text-gray-900 mt-1">{ticket.equipment_category}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Brand/Model</label>
                    <p className="text-gray-900 mt-1">{ticket.brand_model || "-"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Serial Number</label>
                    <p className="text-gray-900 mt-1">{ticket.serial_number || "-"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Created By</label>
                    <p className="text-gray-900 mt-1">{ticket.created_by_name}</p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Problem Description</label>
                  <p className="text-gray-900 mt-1 whitespace-pre-wrap">{ticket.description}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Actions Already Attempted</label>
                  <p className="text-gray-900 mt-1 whitespace-pre-wrap">{ticket.actions_attempted || "-"}</p>
                </div>

                {ticket.parent_job_number && (
                  <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                    <p className="text-sm text-indigo-700">
                      <span className="font-medium">Follow-up to:</span> {ticket.parent_job_number}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Photos */}
            {ticket.photos && ticket.photos.length > 0 && (
              <Card className="bg-white border-gray-200">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Image className="w-5 h-5 text-indigo-600" />
                    Photos ({ticket.photos.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {ticket.photos.map((photo, index) => {
                      const photoUrl = typeof photo === "string" ? photo : (photo?.url || "");
                      const absoluteUrl = photoUrl ? `${BACKEND_URL}${photoUrl}` : "";
                      return (
                        <a
                          key={photoUrl || `photo-${index}`}
                          href={absoluteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="aspect-square rounded-lg overflow-hidden hover:opacity-90 transition-opacity"
                          data-testid={`ticket-photo-${index}`}
                        >
                          <img
                            src={absoluteUrl}
                            alt={`Photo ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </a>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Payment Status (Treasurer / SystemAdmin) */}
            {(ticket.payment?.confirmed ||
              (canConfirmPayment && ticket.status === "Repair Authorized")) && (
              <Card
                className={ticket.payment?.confirmed
                  ? "bg-green-50 border-green-200"
                  : "bg-emerald-50 border-emerald-200"}
                data-testid="payment-status-card"
              >
                <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      ticket.payment?.confirmed ? "bg-green-600" : "bg-emerald-600"
                    }`}>
                      {ticket.payment?.confirmed ? (
                        <CheckCircle2 className="w-5 h-5 text-white" />
                      ) : (
                        <DollarSign className="w-5 h-5 text-white" />
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {ticket.payment?.confirmed ? "Payment confirmed" : "Awaiting payment confirmation"}
                      </p>
                      {ticket.payment?.confirmed ? (
                        <div className="text-sm text-gray-700 mt-1 space-y-0.5">
                          <p>Date: <strong>{ticket.payment.payment_date}</strong></p>
                          {ticket.payment.reference && (
                            <p>Reference: <strong>{ticket.payment.reference}</strong></p>
                          )}
                          {ticket.payment.confirmed_by_name && (
                            <p className="text-xs text-gray-500">
                              Confirmed by {ticket.payment.confirmed_by_name}
                              {ticket.payment.source === "email_link" ? " (via email link)" : ""}
                            </p>
                          )}
                          {ticket.payment.notes && (
                            <p className="text-xs text-gray-600 italic mt-1">"{ticket.payment.notes}"</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-600 mt-0.5">
                          The treasurer can mark this ticket as paid using the link in their email
                          or directly here.
                        </p>
                      )}
                    </div>
                  </div>
                  {!ticket.payment?.confirmed && canConfirmPayment && (
                    <Button
                      onClick={() => {
                        const today = new Date();
                        setPaymentDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`);
                        setPaymentDialogOpen(true);
                      }}
                      className="bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap"
                      data-testid="mark-paid-btn"
                    >
                      Mark as Paid
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Resend Approval Request - only while ticket is still pending approval and not paid */}
            {ticket.status === "Pending Cost Approval" &&
              !ticket.payment?.confirmed &&
              ["SystemAdmin", "Store Manager", "HelpDesk"].includes(user?.role) && (
                <Card className="bg-amber-50 border-amber-200" data-testid="resend-approval-card">
                  <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Approval pending</p>
                        <p className="text-sm text-gray-600 mt-0.5">
                          Did the approver miss the email? Resend the approval request to the
                          current step's recipients without changing the workflow.
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={handleResendApproval}
                      disabled={submitting}
                      variant="outline"
                      className="border-amber-300 text-amber-800 hover:bg-amber-100 whitespace-nowrap"
                      data-testid="resend-approval-btn"
                    >
                      {submitting ? "Sending..." : "Resend Approval Request"}
                    </Button>
                  </CardContent>
                </Card>
              )}

            {/* Resend Treasurer Payment Request - only while awaiting payment */}
            {ticket.status === "Repair Authorized" &&
              !ticket.payment?.confirmed &&
              ["SystemAdmin", "Store Manager", "HelpDesk", "Finance"].includes(user?.role) && (
                <Card className="bg-amber-50 border-amber-200" data-testid="resend-treasurer-card">
                  <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Payment confirmation pending</p>
                        <p className="text-sm text-gray-600 mt-0.5">
                          Treasurer hasn't confirmed payment yet. Resend the payment request email
                          to the market's treasurer(s).
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={handleResendTreasurer}
                      disabled={submitting}
                      variant="outline"
                      className="border-amber-300 text-amber-800 hover:bg-amber-100 whitespace-nowrap"
                      data-testid="resend-treasurer-btn"
                    >
                      {submitting ? "Sending..." : "Resend Payment Request"}
                    </Button>
                  </CardContent>
                </Card>
              )}

            {/* Submit Cost Estimate CTA (Technician/Vendor/SystemAdmin) */}
            {canSubmitCostEstimate(ticket, user) && (  <Card className="bg-indigo-50 border-indigo-200" data-testid="cost-estimate-cta-card">
                <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
                      <DollarSign className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {ticket.cost_estimate ? "Update Cost Estimate" : "Submit Cost Estimate"}
                      </p>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {ticket.status === "Rejected - Needs Review"
                          ? "This estimate was rejected — review the reason and resubmit a revised estimate."
                          : "Provide a diagnosis, repair plan, parts and labor cost so this can move into the approval chain."}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => setEstimateDialogOpen(true)}
                    className="bg-indigo-600 hover:bg-indigo-700 whitespace-nowrap"
                    data-testid="open-cost-estimate-btn"
                  >
                    {ticket.cost_estimate ? "Update Estimate" : "Submit Estimate"}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Cost Estimate */}
            {ticket.cost_estimate && (
              <Card className="bg-white border-gray-200">
                <CardHeader className="flex flex-row items-center justify-between gap-3">
                  <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-indigo-600" />
                    Cost Estimate
                  </CardTitle>
                  {user?.role === "SystemAdmin" && ticket.status !== "Paid" && ticket.status !== "Resolved" && ticket.status !== "Cancelled" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      onClick={() => setAdminEditConfirmOpen(true)}
                      data-testid="admin-edit-estimate-btn"
                    >
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      Edit (Admin)
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Diagnosis</label>
                    <p className="text-gray-900 mt-1">{ticket.cost_estimate.diagnosis}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Repair Plan</label>
                    <p className="text-gray-900 mt-1">{ticket.cost_estimate.repair_plan}</p>
                  </div>

                  {ticket.cost_estimate.parts && ticket.cost_estimate.parts.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 mb-2 block">Parts</label>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 text-gray-500">Part</th>
                            <th className="text-right py-2 text-gray-500">Qty</th>
                            <th className="text-right py-2 text-gray-500">Unit Cost</th>
                            <th className="text-right py-2 text-gray-500">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ticket.cost_estimate.parts.map((part, i) => (
                            <tr key={`${part.name || "part"}-${i}`} className="border-b border-gray-100">
                              <td className="py-2">{part.name}</td>
                              <td className="py-2 text-right tabular-nums">{part.quantity}</td>
                              <td className="py-2 text-right tabular-nums">${part.unit_cost?.toFixed(2)}</td>
                              <td className="py-2 text-right tabular-nums">${(part.quantity * part.unit_cost)?.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="pt-4 border-t border-gray-200 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Parts Total</span>
                      <span className="font-medium tabular-nums">${ticket.cost_estimate.parts_total?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Labor</span>
                      <span className="font-medium tabular-nums">${ticket.cost_estimate.labor_cost?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-semibold">
                      <span>Total</span>
                      <span className="text-indigo-600 tabular-nums">${ticket.cost_estimate.total_cost?.toFixed(2)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Approvals */}
            {ticket.approvals && ticket.approvals.length > 0 && (
              <Card className="bg-white border-gray-200">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-900">Approval History</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {ticket.approvals.map((approval, i) => (
                      <div
                        key={`${approval.timestamp || ""}-${approval.user_name || i}`}
                        className={`flex items-start gap-3 p-3 rounded-lg ${
                          approval.approved ? "bg-green-50" : "bg-red-50"
                        }`}
                      >
                        {approval.approved ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900">
                              {approval.user_name} ({approval.role})
                            </span>
                            <span className="text-sm text-gray-500">
                              {format(new Date(approval.timestamp), "MMM d, yyyy HH:mm")}
                            </span>
                          </div>
                          {!approval.approved && approval.reason && (
                            <p className="text-sm text-red-600 mt-1">Reason: {approval.reason}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Troubleshooting Checklist */}
            {isTroubleshooting && (
              <Card className="bg-white border-gray-200 border-l-4 border-l-amber-500">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-500" />
                    Troubleshooting — {ticket.equipment_category}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Complete the checklist below, then mark as resolved or escalate to Help Desk.
                  </p>
                  {checklistLoaded && checklist.length > 0 ? (
                    <div className="space-y-3">
                      {checklist.map((item, index) => (
                        <label
                          key={item.text || `cl-${index}`}
                          className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={item.checked}
                            onChange={() => {
                              setChecklist(prev => prev.map((c, i) =>
                                i === index ? { ...c, checked: !c.checked } : c
                              ));
                            }}
                            className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            data-testid={`checklist-item-${index}`}
                          />
                          <span className={`text-sm ${item.checked ? "text-gray-400 line-through" : "text-gray-700"}`}>
                            {item.text}
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : !checklistLoaded ? (
                    <p className="text-sm text-gray-400 italic py-2">Loading checklist...</p>
                  ) : null}
                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    <Button
                      onClick={() => handleTroubleshootingSubmit(true)}
                      disabled={submitting}
                      className="bg-green-600 hover:bg-green-700 flex-1"
                      data-testid="resolve-by-manager-btn"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      {submitting ? "Submitting..." : "Issue Resolved - Close Ticket"}
                    </Button>
                    <Button
                      onClick={() => {
                        // New flow: open the Purchase-Details gate FIRST.
                        // It leads into the existing Escalate dialog.
                        setSelectedTechEmail("");
                        setEscalatePick("");
                        setPurchaseLinks([{ url: "", note: "" }]);
                        setPurchaseNewPhotos([]);
                        setPurchaseEditMode(false);
                        setPurchaseDialogOpen(true);
                      }}
                      disabled={submitting}
                      variant="outline"
                      className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid="escalate-helpdesk-btn"
                    >
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Issue Not Resolved - Escalate to Technician
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Completed Troubleshooting */}
            {ticket.troubleshooting && (
              <Card className="bg-white border-gray-200">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    Troubleshooting Completed
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {ticket.troubleshooting.items?.map((item, i) => (
                      <div key={item.text || `ts-${i}`} className="flex items-center gap-2 text-sm">
                        {item.checked ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 text-gray-300 flex-shrink-0" />
                        )}
                        <span className={item.checked ? "text-gray-700" : "text-gray-400"}>{item.text}</span>
                      </div>
                    ))}
                    <p className="text-sm text-gray-500 mt-3 pt-3 border-t border-gray-200">
                      Outcome: <span className="font-medium">{ticket.troubleshooting.resolved ? "Resolved by Manager" : "Escalated to Technician"}</span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Purchase Research — ALWAYS visible on every open ticket as a
                placeholder so the owner can research and attach cart links /
                a cart photo at any time. */}
            {(() => {
              const pd = ticket.purchase_details;
              const canEditPd = !ticket.status?.toLowerCase?.().startsWith("resolved");
              return (
                <Card className="bg-white border-amber-200" data-testid="purchase-details-card">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-amber-600" />
                      Purchase Research
                    </CardTitle>
                    {pd ? (
                      <p className="text-xs text-gray-500 pt-0.5">
                        Submitted by{" "}
                        <span className="font-medium">{pd.submitted_by_name || "—"}</span>
                        {pd.submitted_at && (
                          <>
                            {" "}on{" "}
                            {format(new Date(pd.submitted_at), "MMM d, yyyy h:mm a")}
                          </>
                        )}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500 pt-0.5">
                        Attach the shopping-cart link(s) and a picture of what's
                        needed to fix this ticket. The technician will see them
                        before arriving on-site.
                      </p>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {pd?.sourced_by_technician && (
                      <div className="flex items-center gap-2 text-sm font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                        <Wrench className="w-4 h-4" />
                        Items will be sourced by the technician on-site.
                      </div>
                    )}
                    {(pd?.links || []).length > 0 ? (
                      <ul className="space-y-2">
                        {pd.links.map((l, i) => (
                          <li
                            key={`pl-${i}`}
                            className="flex items-start justify-between gap-3 p-2.5 rounded-md bg-amber-50/60 border border-amber-100"
                          >
                            <div className="min-w-0">
                              <a
                                href={l.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-medium text-indigo-700 hover:underline break-all"
                                data-testid={`purchase-link-${i}`}
                              >
                                {l.url}
                              </a>
                              {l.note && (
                                <p className="text-xs text-gray-600 mt-0.5">{l.note}</p>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : !pd?.sourced_by_technician ? (
                      <div className="text-sm text-gray-400 italic border border-dashed border-amber-200 rounded-md px-3 py-4 text-center">
                        No shopping-cart links yet. Use the button below to add them.
                      </div>
                    ) : null}

                    {canEditPd && (
                      <Button
                        onClick={() => {
                          setPurchaseLinks(
                            (pd?.links || []).length > 0
                              ? pd.links.map((l) => ({ url: l.url, note: l.note || "" }))
                              : [{ url: "", note: "" }]
                          );
                          setPurchaseNewPhotos([]);
                          setPurchaseEditMode(true);
                          setPurchaseDialogOpen(true);
                        }}
                        variant="outline"
                        size="sm"
                        className="w-full border-amber-300 text-amber-800 hover:bg-amber-50 mt-1"
                        data-testid="edit-purchase-details-btn"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        {pd ? "Update Links / Picture" : "Add Shopping-Cart Link & Picture"}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })()}

            {/* Notes Section */}
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-900">Notes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {ticket.notes && ticket.notes.length > 0 && (
                  <div className="space-y-3">
                    {ticket.notes.map((note) => (
                      <div key={note.id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-900">{note.author_name}</span>
                          <span className="text-xs text-gray-500">{format(new Date(note.created_at), "MMM d, HH:mm")}</span>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.content}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Add a note..."
                    value={noteContent}
                    onChange={(e) => setNoteContent(e.target.value)}
                    rows={2}
                    className="flex-1"
                    data-testid="note-input"
                  />
                  <Button
                    onClick={handleAddNote}
                    disabled={!noteContent.trim()}
                    className="bg-indigo-600 hover:bg-indigo-700 self-end"
                    data-testid="add-note-btn"
                  >
                    Add
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Timeline Sidebar */}
          <div className="space-y-6">
            {/* Quick Info */}
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-900">Quick Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Created</p>
                    <p className="font-medium text-gray-900">
                      {format(new Date(ticket.created_at), "MMM d, yyyy HH:mm")}
                    </p>
                  </div>
                </div>

                {ticket.assignee_name && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                      <User className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Assigned To</p>
                      <p className="font-medium text-gray-900">{ticket.assignee_name}</p>
                      <p className="text-xs text-gray-500 capitalize">{ticket.assignee_type}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-medium text-gray-900">{ticket.restaurant_name}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                    <Wrench className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Equipment</p>
                    <p className="font-medium text-gray-900">{ticket.equipment_category}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Timeline */}
            {ticket.timeline && ticket.timeline.length > 0 && (
              <Card className="bg-white border-gray-200">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-900">Activity Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {ticket.timeline.map((entry, index) => (
                      <div key={entry.id || index} className="timeline-item relative pl-8">
                        <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center">
                          <div className="w-2 h-2 rounded-full bg-indigo-600"></div>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{entry.action.replace(/_/g, " ")}</p>
                          <p className="text-xs text-gray-500">
                            {entry.user_name} - {format(new Date(entry.timestamp), "MMM d, HH:mm")}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Cancel Dialog */}
        <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancel Ticket</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to cancel ticket {ticket.job_number}? Please provide a reason.
              </p>
              <Textarea
                placeholder="Reason for cancellation..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                data-testid="cancel-reason-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>Back</Button>
              <Button
                onClick={handleCancelTicket}
                disabled={submitting}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-cancel-btn"
              >
                {submitting ? "Cancelling..." : "Confirm Cancel"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reopen Dialog */}
        <Dialog open={reopenDialogOpen} onOpenChange={setReopenDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reopen Ticket</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-gray-600 mb-4">
                This will create a follow-up ticket linked to {ticket.job_number}. Please describe why the issue needs to be reopened.
              </p>
              <Textarea
                placeholder="Describe the reason for reopening..."
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                rows={4}
                data-testid="reopen-reason-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReopenDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleReopen} 
                disabled={submitting}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="confirm-reopen-btn"
              >
                {submitting ? "Creating..." : "Create Follow-up"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog (SystemAdmin only) */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent data-testid="delete-ticket-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <Trash2 className="w-5 h-5" />
                Permanently Delete Ticket
              </DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-3">
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
                <strong>This action cannot be undone.</strong> The ticket, all photos, audit logs,
                approval tokens, and treasurer payment links will be permanently removed.
              </div>
              <p className="text-sm text-gray-700">
                To confirm, type the job number{" "}
                <span className="font-mono font-semibold text-gray-900">{ticket.job_number}</span>{" "}
                below.
              </p>
              <Input
                placeholder={ticket.job_number}
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="font-mono"
                data-testid="delete-confirm-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleDeleteTicket}
                disabled={submitting || deleteConfirmText !== ticket.job_number}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-btn"
              >
                {submitting ? "Deleting..." : "Delete Permanently"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Escalation Picker Dialog */}
        <Dialog open={escalateDialogOpen} onOpenChange={setEscalateDialogOpen}>
          <DialogContent className="sm:max-w-md p-0 overflow-hidden" data-testid="escalate-dialog">
            <DialogHeader className="px-6 pt-5 pb-3 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-amber-50">
              <DialogTitle className="flex items-center gap-2 text-orange-800 text-lg">
                <AlertTriangle className="w-5 h-5" />
                Escalate to Help Desk
              </DialogTitle>
            </DialogHeader>
            <div className="px-6 py-5 space-y-5">
              <p className="text-sm text-gray-600 leading-relaxed">
                Pick a DO/DM for this market (the ticket auto-routes to the
                technician they manage) OR pick a specific technician directly.
              </p>

              {opsUsers.length > 0 && (
                <div className="space-y-2.5">
                  <Label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                    <UserCheck className="w-4 h-4 text-indigo-600" />
                    Assign to a DO / DM
                  </Label>
                  <Select
                    value={selectedOpsUserId}
                    onValueChange={(v) => {
                      setSelectedOpsUserId(v);
                      setSelectedTechEmail("");
                    }}
                  >
                    <SelectTrigger data-testid="escalate-ops-select" className="h-10">
                      <SelectValue placeholder="Select a DO / DM..." />
                    </SelectTrigger>
                    <SelectContent>
                      {opsUsers.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {o.full_name}{" "}
                              <span className="text-xs text-gray-500 font-normal">({o.role})</span>
                            </span>
                            <span className="text-[11px] text-gray-500">
                              {o.default_technician_email
                                ? `→ ${o.default_technician_name || o.default_technician_email}`
                                : "No default technician assigned"}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedOpsUserId && (() => {
                    const picked = opsUsers.find((o) => o.id === selectedOpsUserId);
                    if (!picked) return null;
                    if (picked.default_technician_email) {
                      return (
                        <div className="text-[12px] bg-indigo-50 border border-indigo-100 text-indigo-800 rounded-md px-3 py-2">
                          Will route to{" "}
                          <span className="font-semibold">
                            {picked.default_technician_name || picked.default_technician_email}
                          </span>{" "}
                          via {picked.full_name}.
                        </div>
                      );
                    }
                    return (
                      <div className="text-[12px] bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2">
                        {picked.full_name} has no default technician yet — set one in Admin Panel → Active Users, or pick a technician below.
                      </div>
                    );
                  })()}
                  <Button
                    onClick={handleEscalateViaOpsUser}
                    disabled={
                      submitting ||
                      !selectedOpsUserId ||
                      !opsUsers.find((o) => o.id === selectedOpsUserId)?.default_technician_email
                    }
                    className="w-full h-10 bg-indigo-600 hover:bg-indigo-700 text-white font-medium shadow-sm disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
                    data-testid="assign-via-ops-btn"
                  >
                    {submitting ? "Sending..." : "Route via DO/DM to their Technician"}
                  </Button>
                </div>
              )}

              {opsUsers.length > 0 && marketTechs.length > 0 && (
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-[11px] uppercase">
                    <span className="bg-white px-3 text-gray-400 tracking-wider">or</span>
                  </div>
                </div>
              )}

              {marketTechs.length > 0 && (
                <div className="space-y-2.5">
                  <Label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                    <UserCheck className="w-4 h-4 text-indigo-600" />
                    Assign to a specific technician
                  </Label>
                  <Select
                    value={selectedTechEmail}
                    onValueChange={(v) => {
                      setSelectedTechEmail(v);
                      setSelectedOpsUserId("");
                    }}
                  >
                    <SelectTrigger data-testid="escalate-tech-select" className="h-10">
                      <SelectValue placeholder="Select a technician..." />
                    </SelectTrigger>
                    <SelectContent>
                      {marketTechs.map((t) => (
                        <SelectItem key={t.email} value={t.email}>
                          {t.name ? `${t.name} — ${t.email}` : t.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={() => handleTroubleshootingSubmit(false, selectedTechEmail)}
                    disabled={submitting || !selectedTechEmail}
                    variant="outline"
                    className="w-full h-10 border-indigo-300 text-indigo-700 hover:bg-indigo-50 hover:border-indigo-400 font-medium disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    data-testid="assign-specific-tech-btn"
                  >
                    {submitting ? "Sending..." : "Assign & Notify Selected Technician"}
                  </Button>
                </div>
              )}

              {opsUsers.length === 0 && marketTechs.length === 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
                  No DOs/DMs or technicians available for this market yet. Configure them in <span className="font-medium">Admin Panel</span>.
                </div>
              )}

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-[11px] uppercase">
                  <span className="bg-white px-3 text-gray-400 tracking-wider">or</span>
                </div>
              </div>

              <Button
                onClick={() => handleTroubleshootingSubmit(false, null)}
                disabled={submitting}
                variant="outline"
                className="w-full h-10 border-orange-300 text-orange-700 hover:bg-orange-50 hover:border-orange-400 font-medium"
                data-testid="assign-all-techs-btn"
              >
                <Users className="w-4 h-4 mr-2" />
                Make available to all market technicians
              </Button>
            </div>
            <DialogFooter className="px-6 py-3 border-t border-gray-100 bg-gray-50 sm:justify-end">
              <Button
                variant="ghost"
                onClick={() => setEscalateDialogOpen(false)}
                className="text-gray-600 hover:text-gray-900"
              >
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <CostEstimateDialog
          ticket={ticket}
          open={estimateDialogOpen}
          onOpenChange={(open) => {
            setEstimateDialogOpen(open);
            if (!open) setAdminEditEstimateMode(false);
          }}
          onSubmitted={fetchTicket}
          adminEditMode={adminEditEstimateMode}
        />

        {/* Admin: confirm cost estimate edit resets approval chain */}
        <Dialog open={adminEditConfirmOpen} onOpenChange={setAdminEditConfirmOpen}>
          <DialogContent className="sm:max-w-md" data-testid="admin-edit-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="w-5 h-5" />
                Edit will reset the approval chain
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2 text-sm text-gray-700">
              <p>
                You are about to edit the cost estimate for ticket
                <strong> {ticket?.job_number}</strong>.
              </p>
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900">
                <p className="font-semibold mb-1">Changing the amount will:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Clear all previous approval records on this ticket</li>
                  <li>Restart the approval workflow from the new total</li>
                  <li>Send a fresh approval email to the correct approver tier</li>
                </ul>
              </div>
              <p>Do you want to continue?</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAdminEditConfirmOpen(false)} data-testid="admin-edit-confirm-cancel">
                Cancel
              </Button>
              <Button
                className="bg-amber-600 hover:bg-amber-700"
                onClick={() => {
                  setAdminEditConfirmOpen(false);
                  setAdminEditEstimateMode(true);
                  setEstimateDialogOpen(true);
                }}
                data-testid="admin-edit-confirm-continue"
              >
                Continue to Edit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent data-testid="mark-paid-dialog">
            <DialogHeader>
              <DialogTitle>Mark Payment as Paid</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Payment Date *</Label>
                <Input
                  type="date"
                  value={paymentDate}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  data-testid="in-app-payment-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Reference / Check # (optional)</Label>
                <Input
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="e.g. Check #4521 or ACH ID"
                  data-testid="in-app-payment-ref"
                />
              </div>
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  rows={2}
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  data-testid="in-app-payment-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmPayment}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="in-app-payment-submit"
              >
                {submitting ? "Confirming…" : "Confirm Payment"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ======== Purchase Details Gate (pre-escalation) ======== */}
        <Dialog
          open={purchaseDialogOpen}
          onOpenChange={(open) => {
            setPurchaseDialogOpen(open);
            if (!open) setPurchaseEditMode(false);
          }}
        >
          <DialogContent className="sm:max-w-2xl p-0 overflow-hidden" data-testid="purchase-dialog">
            <DialogHeader className="px-6 pt-5 pb-3 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50">
              <DialogTitle className="flex items-center gap-2 text-amber-900 text-lg">
                <FileText className="w-5 h-5" />
                Items Needed to Fix the Issue
              </DialogTitle>
              <p className="text-sm text-amber-800/90 mt-1">
                Before escalating, upload a <strong>link + a picture</strong> of
                the shopping cart for every item needed to fix this issue.
              </p>
              <div className="mt-3 text-xs text-amber-900/90 bg-white/70 border border-amber-200 rounded-md px-3 py-2 leading-relaxed">
                <strong>Tip:</strong> paste the problem description along with the
                equipment <em>brand/model</em> and <em>serial number</em> into
                <strong> Microsoft Copilot</strong> or <strong>ChatGPT</strong> —
                ask it to find the exact replacement part on Amazon, Grainger, or
                the manufacturer's site, then drop the link below and attach a
                screenshot of the cart.
              </div>
            </DialogHeader>
            <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-gray-800">
                    Shopping-cart link(s) for the needed items
                  </Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setPurchaseLinks((prev) => [...prev, { url: "", note: "" }])}
                    className="h-7 text-xs"
                    data-testid="purchase-add-link-btn"
                  >
                    + Add link
                  </Button>
                </div>
                <div className="space-y-2.5">
                  {purchaseLinks.map((row, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2">
                      <Input
                        value={row.url}
                        onChange={(e) =>
                          setPurchaseLinks((prev) =>
                            prev.map((r, i) => (i === idx ? { ...r, url: e.target.value } : r))
                          )
                        }
                        placeholder="https://..."
                        className="col-span-6"
                        data-testid={`purchase-link-url-${idx}`}
                      />
                      <Input
                        value={row.note}
                        onChange={(e) =>
                          setPurchaseLinks((prev) =>
                            prev.map((r, i) => (i === idx ? { ...r, note: e.target.value } : r))
                          )
                        }
                        placeholder="Short note (part, qty…)"
                        className="col-span-5"
                        data-testid={`purchase-link-note-${idx}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() =>
                          setPurchaseLinks((prev) =>
                            prev.length === 1 ? [{ url: "", note: "" }] : prev.filter((_, i) => i !== idx)
                          )
                        }
                        className="col-span-1 text-gray-400 hover:text-red-600 hover:bg-red-50 px-2"
                        data-testid={`purchase-link-remove-${idx}`}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-800">
                  Picture of the shopping cart / needed item
                </Label>
                <p className="text-xs text-gray-500">
                  Attach a screenshot of the Amazon/Grainger cart and/or a clear
                  photo of the broken part so the technician arrives prepared.
                </p>
                <Input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setPurchaseNewPhotos((prev) => [...prev, ...files]);
                    e.target.value = "";
                  }}
                  className="cursor-pointer"
                  data-testid="purchase-photos-input"
                />
                {purchaseNewPhotos.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {purchaseNewPhotos.map((f, i) => (
                      <span
                        key={`${f.name}-${i}`}
                        className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200"
                      >
                        <Image className="w-3 h-3" />
                        {f.name}
                        <button
                          type="button"
                          onClick={() => setPurchaseNewPhotos((prev) => prev.filter((_, idx) => idx !== i))}
                          className="ml-0.5 text-indigo-500 hover:text-red-600"
                          aria-label="Remove"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {(ticket?.photos?.length || 0) > 0 && (
                  <div className="text-[11px] text-gray-500 pt-1">
                    {ticket.photos.length} photo(s) already attached to this ticket.
                  </div>
                )}
              </div>
            </div>
            <DialogFooter className="px-6 pb-5 pt-2 gap-2 flex-col sm:flex-row">
              <Button
                variant="outline"
                onClick={() => setPurchaseDialogOpen(false)}
                disabled={purchaseSubmitting}
                data-testid="purchase-cancel-btn"
                className="sm:mr-auto"
              >
                Cancel
              </Button>
              {!purchaseEditMode && (
                <Button
                  variant="outline"
                  onClick={() => handleSubmitPurchaseDetails(true)}
                  disabled={purchaseSubmitting}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 text-left h-auto py-2 whitespace-normal"
                  data-testid="purchase-skip-tech-sources-btn"
                  title="Skip the research — the technician will source what's needed on-site"
                >
                  <Wrench className="w-4 h-4 mr-2 shrink-0" />
                  Items will be sourced by the technician on-site — no info available at the moment
                </Button>
              )}
              <Button
                onClick={() => handleSubmitPurchaseDetails(false)}
                disabled={purchaseSubmitting}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="purchase-requires-tech-btn"
              >
                <AlertCircle className="w-4 h-4 mr-2" />
                {purchaseSubmitting
                  ? "Saving…"
                  : purchaseEditMode
                    ? "Save"
                    : "Submit & Pick Assignee"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ======== Reassign Dialog (SystemAdmin / President) ======== */}
        <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
          <DialogContent className="sm:max-w-lg p-0 overflow-hidden" data-testid="reassign-dialog">
            <DialogHeader className="px-6 pt-5 pb-3 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-sky-50">
              <DialogTitle className="flex items-center gap-2 text-indigo-900 text-lg">
                <UserCheck className="w-5 h-5" />
                Reassign Ticket
              </DialogTitle>
              <p className="text-sm text-indigo-800/80 mt-1">
                Redirect this ticket to any user in the market — GM, DO/DM,
                technician, or help desk. Current assignee:{" "}
                <span className="font-medium">
                  {ticket.assignee_name || "Unassigned"}
                </span>
              </p>
            </DialogHeader>
            <div className="px-6 py-5 space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-800">New assignee</Label>
                <Select value={reassignTargetId} onValueChange={(v) => setReassignTargetId(v)}>
                  <SelectTrigger data-testid="reassign-user-select" className="h-10">
                    <SelectValue placeholder="Select a user..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {assignableUsers.length === 0 && (
                      <div className="px-2 py-3 text-xs text-gray-500">
                        No active users available for this market.
                      </div>
                    )}
                    {(() => {
                      const grouped = assignableUsers.reduce((acc, u) => {
                        (acc[u.role] = acc[u.role] || []).push(u);
                        return acc;
                      }, {});
                      return Object.entries(grouped).map(([role, list]) => (
                        <div key={role}>
                          <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500 bg-gray-50">
                            {role}
                          </div>
                          {list.map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.full_name} — {u.email}
                            </SelectItem>
                          ))}
                        </div>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-800">
                  Reason <span className="text-xs text-gray-400 font-normal">(optional, shown in audit log)</span>
                </Label>
                <Textarea
                  value={reassignReason}
                  onChange={(e) => setReassignReason(e.target.value)}
                  placeholder="Why is this ticket being redirected?"
                  rows={3}
                  className="resize-none"
                  data-testid="reassign-reason-input"
                />
              </div>
            </div>
            <DialogFooter className="px-6 pb-5 pt-2 gap-2">
              <Button
                variant="outline"
                onClick={() => setReassignDialogOpen(false)}
                disabled={reassignSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleReassignSubmit}
                disabled={reassignSubmitting || !reassignTargetId}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="reassign-confirm-btn"
              >
                {reassignSubmitting ? "Reassigning…" : "Confirm Reassignment"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
